import { format } from 'date-fns';
import { AdviseCurrentSpend, AdviseEngagement, CurrentSpendSource } from '../../../../types/advise';
import { dollars } from './currentSupplierSpend';
import { billingPeriodStart } from './InvoiceView.calc';

export const MONTHLY_CX_SPEND_KEY = 'monthly_cx_spend';

const LEGACY_SPEND_KEYS = [
  'monthly_platform',
  'monthly_telco',
  'monthly_wfm',
  'monthly_qm',
  'monthly_recording',
  'monthly_other'
];

type RoiAnalysis = Record<string, unknown>;
type SpendMap = Record<string, unknown>;

const isNumber = (value: unknown): value is number => typeof value === 'number' && !isNaN(value);

const billingMonth = (period: string | null) => {
  const start = billingPeriodStart(period);
  return start && format(start, 'MMM yyyy');
};

export const sourceLabel = ({ basis, billing_period }: CurrentSpendSource) => {
  if (basis === 'invoice') {
    const month = billingMonth(billing_period);
    return month ? `(${month} invoice)` : '(invoice)';
  }
  return basis === 'discovery' ? '(Discovery)' : '(manual)';
};

export const sourceLine = (spend: AdviseCurrentSpend): string | null => {
  if (spend.status === 'skipped') return 'Source: No current supplier';
  if (spend.sources.length === 0) return null;
  const parts = spend.sources.map(
    (source) => `${source.name} ${dollars(source.monthly_amount)} ${sourceLabel(source)}`
  );
  return `Source: ${parts.join(' + ')} · ${dollars(spend.annualized_baseline)} annualized`;
};

export const overriddenLine = (spend: AdviseCurrentSpend): string => {
  const annualized = (spend.effective_monthly_amount ?? 0) * 12;
  const total = dollars(spend.monthly_baseline);
  return `Overridden by advisor · Current Suppliers total is ${total} · ${dollars(annualized)} annualized`;
};

export const isSourcedSpend = (spend?: AdviseCurrentSpend): spend is AdviseCurrentSpend =>
  spend?.status === 'sourced' || spend?.status === 'skipped';

const spendMap = (roi: RoiAnalysis): SpendMap => (roi.currentSpend as SpendMap | undefined) ?? {};

/** What the Advisor typed; an engagement saved before the single field adds up its six monthly costs. */
export const typedMonthlySpend = (roi: RoiAnalysis): number | null => {
  const typed = spendMap(roi);
  if (isNumber(typed[MONTHLY_CX_SPEND_KEY])) return typed[MONTHLY_CX_SPEND_KEY];
  const legacy = LEGACY_SPEND_KEYS.map((key) => typed[key]).filter(isNumber);
  if (!legacy.length) return null;
  return Math.round(legacy.reduce((sum, value) => sum + value, 0) * 100) / 100;
};

export const withTypedMonthlySpend = (roi: RoiAnalysis, amount: number | null): RoiAnalysis => ({
  ...roi,
  currentSpend: amount === null ? {} : { [MONTHLY_CX_SPEND_KEY]: amount }
});

export const roiMonthlySpend = (engagement: Pick<AdviseEngagement, 'current_spend' | 'roi_analysis'>): number =>
  isSourcedSpend(engagement.current_spend)
    ? (engagement.current_spend.effective_monthly_amount ?? 0)
    : (typedMonthlySpend(engagement.roi_analysis ?? {}) ?? 0);
